import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib';

type RadioOption = {
  label: string;
  description: string;
  value: string;
  id: string;
  disabled?: boolean;
  badge?: {
    text: string;
    variant?: 'default' | 'secondary' | 'destructive' | 'outline';
    className?: string;
  };
};

type RadioVariant = 'default' | 'box';

type RadioSelectProps = {
  options: RadioOption[];
  selectedValue: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  variant?: RadioVariant;
};

function BoxStyleOption({ option }: { option: RadioOption }) {
  return (
    <Label
      htmlFor={option.id}
      className="hover:border-primary-400 peer-checked:border-primary-600 peer-checked:ring-primary-600 flex cursor-pointer rounded-lg border border-gray-300 p-4 text-left transition-all hover:bg-primary-50/10 peer-checked:bg-primary-50 peer-checked:ring-1 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
    >
      <div className="flex items-start">
        <RadioGroupItem
          value={option.value}
          id={option.id}
          disabled={option.disabled}
          className="peer mr-3 mt-0.5"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              {option.label}
            </span>
            {option.badge && (
              <Badge
                variant={option.badge.variant || 'default'}
                className={option.badge.className}
              >
                {option.badge.text}
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm text-gray-500">{option.description}</div>
        </div>
      </div>
    </Label>
  );
}

function InlineOption({ option }: { option: RadioOption }) {
  return (
    <>
      <RadioGroupItem
        value={option.value}
        id={option.id}
        disabled={option.disabled}
      />
      <Label
        htmlFor={option.id}
        className="ml-3 leading-6"
      >
        <span className="font-semibold">{option.label}</span>
        <span className="ml-1">
          <span className="sr-only">{option.label} </span>
          {option.description}
        </span>
      </Label>
    </>
  );
}

export function RadioSelect({
  options,
  selectedValue,
  onChange,
  className,
  variant = 'default',
}: RadioSelectProps) {
  const isBoxVariant = variant === 'box';

  return (
    <RadioGroup
      value={selectedValue}
      onValueChange={onChange}
      className={cn(className, {
        'space-y-3': isBoxVariant,
      })}
    >
      {options.map(option => (
        <div
          key={option.id}
          className={cn({
            'relative': isBoxVariant,
            'flex items-center': !isBoxVariant,
            'text-gray-400': option.disabled && !isBoxVariant,
            'text-gray-700': !option.disabled && !isBoxVariant,
          })}
        >
          {isBoxVariant ? (
            <BoxStyleOption option={option} />
          ) : (
            <InlineOption option={option} />
          )}
        </div>
      ))}
    </RadioGroup>
  );
}

export default RadioSelect;
